// A client's quotes (round 43), on the client's own page.
//
// Fetched when the tab is opened, like the Jobs tab beside it. Cards on a
// phone and a table on a desktop, from one sorted array (round 34's rule).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api';
import { Banner } from '../../ui';
import { CardList, RowCard } from '../ui/RowCard';
import { LinkedRow } from '../ui/LinkedRow';
import { SortHeader } from '../ui/SortHeader';
import { StatusBadge } from '../ui/StatusBadge';
import { sortRows, useSort } from '../../lib/sortRows';
import { formatDate } from '../../lib/dates';
import { QUOTE_STATUS_LABEL, formatCents, quotesApi, type QuoteSummary } from '../../quotesApi';

type SortKey = 'number' | 'title' | 'status' | 'date' | 'total';

export function ClientQuotesPanel({ clientId }: { clientId: string }) {
  const [quotes, setQuotes] = useState<QuoteSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { sortKey, sortDir, onSort } = useSort<SortKey>('date', 'desc');

  useEffect(() => {
    quotesApi.list({ clientId }).then(
      (result) => setQuotes(result.quotes),
      (err: unknown) => setError(err instanceof ApiError ? err.message : 'Could not load the quotes.')
    );
  }, [clientId]);

  const visible = useMemo(
    () =>
      sortRows(
        quotes ?? [],
        (quote) => {
          switch (sortKey) {
            case 'number':
              return quote.number;
            case 'title':
              return quote.title;
            case 'status':
              return QUOTE_STATUS_LABEL[quote.status];
            case 'date':
              return quote.quoteDate;
            case 'total':
              return quote.totalCents;
          }
        },
        sortDir
      ),
    [quotes, sortKey, sortDir]
  );

  return (
    <section className="panel">
      <div className="row mb-3">
        <h2 className="!mb-0">Quotes</h2>
        <span className="spacer" />
        <Link to={`/app/quotes/new?clientId=${clientId}`} className="btn-secondary">
          New quote
        </Link>
      </div>
      {error ? (
        <Banner kind="error">{error}</Banner>
      ) : quotes === null ? (
        <p className="muted">Loading…</p>
      ) : quotes.length === 0 ? (
        <p className="muted">No quotes for this client yet.</p>
      ) : (
        <>
          <CardList>
            {visible.map((quote) => (
              <RowCard
                key={quote.id}
                to={`/app/quotes/${quote.id}`}
                title={
                  <>
                    <span className="tabular-nums">{quote.number}</span> · {quote.title}
                  </>
                }
                badges={<StatusBadge status={quote.status} kind="quote" />}
              >
                <div className="tabular-nums">{formatCents(quote.totalCents)}</div>
                <div>{formatDate(quote.quoteDate)}</div>
              </RowCard>
            ))}
          </CardList>
          <div className="-mx-6 overflow-x-auto border-y border-gray-200 max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Number" sortKey="number" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Title" sortKey="title" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Date" sortKey="date" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Total" sortKey="total" current={sortKey} dir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {visible.map((quote) => (
                  <LinkedRow key={quote.id} to={`/app/quotes/${quote.id}`}>
                    <td className="whitespace-nowrap font-medium tabular-nums">
                      <Link to={`/app/quotes/${quote.id}`} className="table-link">
                        {quote.number}
                      </Link>
                    </td>
                    <td>{quote.title}</td>
                    <td>
                      <StatusBadge status={quote.status} kind="quote" />
                    </td>
                    <td className="table-muted whitespace-nowrap">{formatDate(quote.quoteDate)}</td>
                    <td className="whitespace-nowrap text-right tabular-nums">{formatCents(quote.totalCents)}</td>
                  </LinkedRow>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
